// The lifecycle, as the interface reads it.
//
// The rules themselves live on the server (backend/src/modules/orbit_development/lifecycle.py)
// and are enforced there. Nothing here decides what is allowed — `options.transitions` arrives
// with every response, already narrowed to what THIS caller may do, so the buttons a person
// sees and the moves the server will accept cannot drift apart.
//
// What this file owns is presentation: what each state is called, how it reads at a glance, and
// which of the three views a task belongs in.

// One status per task. The old `status` column ('defined', 'ready', 'in-progress', 'done',
// 'blocked') is legacy and unread since migration 915 — its useful values live here now.
export const LIFECYCLE_LABEL = {
  'defined': 'Defined',
  'ready': 'Ready',
  'in_progress': 'In progress',
  'needs_decision': 'Needs decision',
  'ready_for_test_qa': 'Ready for test',
  'ready_for_prod_qa': 'Ready for prod',
  'done': 'Done',
};

// Colour says who is holding the task, not which step it is on. Grey is nobody yet, brass is
// being worked on, amber is waiting on a person, teal is with QA, green is finished. Which QA
// environment is the word in the label — three shades of "with QA" would be a distinction the
// eye cannot make and the reader does not need.
export const LIFECYCLE_BADGE = {
  'defined': 'o-grey',
  'ready': 'o-grey',
  'in_progress': '',
  'needs_decision': 'o-warn',
  'ready_for_test_qa': 'o-info',
  'ready_for_prod_qa': 'o-info',
  'done': 'o-ok',
};

// What each state means, for the one place there is room to say it.
export const LIFECYCLE_HELP = {
  'defined': 'Written down, but not specified well enough to pick up yet.',
  'ready': 'Specified. Waiting to be started.',
  'in_progress': 'A developer has started.',
  'needs_decision': 'Parked on a product or scope call, not on code.',
  'ready_for_test_qa': 'On test, waiting for QA.',
  'ready_for_prod_qa': 'On production, waiting for the final check.',
  'done': 'Verified working on production.',
};

export const ENVIRONMENT_LABEL = { test: 'Test', production: 'Production' };

// Where a task has just gone, said in terms of what happens to it next.
//
// Every one of these moves takes the task OUT of the view it was moved from — that is what a
// view being a filter means — so the row disappearing is correct and, on its own, looks exactly
// like losing it. This is the sentence that makes the difference.
export const MOVED_ON = {
  'ready': 'is ready to start — it is in My Work',
  'in_progress': 'is in progress',
  'needs_decision': 'is parked for a decision — it is back in the Pipeline',
  'ready_for_test_qa': 'is with QA on test',
  'ready_for_prod_qa': 'passed on test — it is with QA on production now',
  'done': 'is done — it is on the Completed page',
};

// A rejected task sorts to the top of its list for a week after the rejection. Long enough
// that one taken on a Friday is still at the top on Monday; short enough that the list does
// not slowly become a memorial. Ordering only — it never keeps a task in a view its status
// has left.
export const REJECTION_PIN_DAYS = 7;

// Each view is one set of statuses, and the four sets do not overlap: every task is on exactly
// one screen, and moving it moves it between screens. That is what makes a status change
// visible as something happening rather than as a badge quietly changing colour.
//
// My Work is what is yours to act on now — specified and waiting, or already started. `defined`
// is not here: it has not been written up well enough to pick up, which is work of a different
// kind and belongs with the backlog. `needs_decision` is not here either, for the same reason
// from the other end — it is waiting on somebody else.
export const MY_WORK_STATUSES = ['ready', 'in_progress'];
export const QA_STATUSES = ['ready_for_test_qa', 'ready_for_prod_qa'];
export const PIPELINE_STATUSES = ['defined', 'ready', 'needs_decision', 'in_progress'];
export const DONE_STATUSES = ['done'];

// The statuses a view asks the server for when exporting. Keyed off the same constants the
// filtering uses, so a view cannot export a set it does not show.
export const STATUSES_FOR_VIEW = {
  'my-work': MY_WORK_STATUSES,
  'qa-queue': QA_STATUSES,
  'pipeline': PIPELINE_STATUSES,
  'completed': DONE_STATUSES,
};

// In the order work moves: what is mine to do, what is coming, what is being checked, what is
// finished. This array is also the module's navigation — see OrbitDevelopmentApp.
export const VIEWS = [
  { id: 'my-work', label: 'My Work',
    note: 'Ready to start, and started. Anything QA sent back comes first.' },
  { id: 'pipeline', label: 'Pipeline',
    note: 'The backlog and what is being worked on — everything before QA, filed by section.' },
  { id: 'qa-queue', label: 'QA Queue',
    note: 'Waiting to be tested, grouped by the environment it is sitting on.' },
  { id: 'completed', label: 'Completed',
    note: 'Verified on production. The record of what shipped.' },
];

/** The view this role lands on. A tester opening the tool wants the queue, not the backlog. */
export function defaultView(role) {
  if (!role) return 'pipeline';
  if (role.can_qa && !role.can_develop) return 'qa-queue';
  if (role.can_develop) return 'my-work';
  // A Product User owns no transition at all, so neither working view is theirs.
  return 'pipeline';
}

export function isRejected(task, days = REJECTION_PIN_DAYS) {
  if (!task?.last_rejected_at) return false;
  const at = new Date(task.last_rejected_at);
  if (Number.isNaN(at.getTime())) return false;
  return (Date.now() - at.getTime()) <= days * 24 * 60 * 60 * 1000;
}

/**
 * The tasks a view shows.
 *
 * A view is a filter and nothing else — no task lives in a "place", and the same task is in My
 * Work on Monday and the QA Queue on Tuesday without anything having been moved. That is the
 * whole reason the section stopped carrying the workflow.
 *
 * My Work is strictly ready + in_progress. A rejection lands the task back in in_progress, so
 * rejected work is here because of its status, not despite it — and once it is sent back to QA
 * it leaves, the same as any other hand-off. The rejection pin only orders what qualifies.
 */
export function tasksForView(tasks, view) {
  if (view === 'qa-queue') return tasks.filter(t => QA_STATUSES.includes(t.lifecycle_status));
  if (view === 'completed') return tasks.filter(t => DONE_STATUSES.includes(t.lifecycle_status));
  if (view === 'my-work') {
    return tasks.filter(t => MY_WORK_STATUSES.includes(t.lifecycle_status));
  }
  // The Pipeline is everything before QA. It stopped being "everything" when Completed became
  // its own page: a backlog that also lists finished work is one you scroll past to reach.
  return tasks.filter(t => PIPELINE_STATUSES.includes(t.lifecycle_status));
}

/** Rejected first, newest rejection at the very top; everything else in the order given. */
export function pinRejected(tasks) {
  // Not `filter(isRejected)`: filter would pass the array index as the `days` parameter.
  const rejected = tasks.filter(t => isRejected(t))
    .sort((a, b) => new Date(b.last_rejected_at) - new Date(a.last_rejected_at));
  const rest = tasks.filter(t => !isRejected(t));
  return [...rejected, ...rest];
}

/**
 * [{ key, label, tasks }] — tasks grouped by the module they are against.
 *
 * Not the section. A section is filing somebody chose and can say anything; `target_module` is
 * a fact about the task, which is what makes these groups safe to build without offering any
 * way to rearrange them — there is nothing here that a person owns.
 *
 * Modules keep the order they appear in the registry, so the grouping does not reshuffle as
 * tasks move. Inside a group, anything QA has sent back comes first: it is the work that was
 * already thought finished, and it is the reason this view is opened first thing.
 */
export function groupByModule(tasks, modules = []) {
  const order = modules.map(m => m.id);
  const seen = [...new Set(tasks.map(t => t.target_module))]
    .sort((a, b) => {
      const ai = order.indexOf(a); const bi = order.indexOf(b);
      return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
    });
  return seen.map(id => ({
    key: id,
    label: modules.find(m => m.id === id)?.label || id,
    tasks: pinRejected(tasks.filter(t => t.target_module === id)),
  }));
}

/** [{ environment, label, tasks }] — the QA queue split by the box being tested. */
export function groupByEnvironment(tasks, environments = {}) {
  return ['test', 'production'].map(env => ({
    environment: env,
    label: ENVIRONMENT_LABEL[env],
    tasks: tasks.filter(t => environments[t.lifecycle_status] === env),
  }));
}
